/**
 * Recipe Context
 * Manages recipe-related state across components without localStorage
 * Uses URL parameters and React state for better data flow
 */

import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { URL_PARAMS } from '@/constants/recipe/recipe.constants';

const RecipeContext = createContext(null);

export const useRecipeContext = () => {
  const context = useContext(RecipeContext);
  if (!context) {
    throw new Error('useRecipeContext must be used within RecipeProvider');
  }
  return context;
};

export const RecipeProvider = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  // Get initial values from URL or location state
  const initialProductId =
    searchParams.get(URL_PARAMS.PRODUCT_ID) ||
    location.state?.productId;

  const initialRecipeId =
    searchParams.get(URL_PARAMS.RECIPE_ID) ||
    location.state?.recipeId;

  // Core recipe state
  const [productId, setProductIdState] = useState(initialProductId);
  const [recipeId, setRecipeIdState] = useState(initialRecipeId);
  const [recipeName, setRecipeName] = useState(location.state?.recipeName || '');
  const [productName, setProductName] = useState('');

  // Process related state (replaces localStorage.setItem('last_process_op_id', ...))
  const [lastProcessOpId, setLastProcessOpId] = useState(0);
  const [processCount, setProcessCount] = useState(0);

  // Edit process data (replaces localStorage.setItem('editProcess', ...))
  const [editProcessData, setEditProcessData] = useState(null);

  // Sync with URL params
  useEffect(() => {
    const params = {};
    if (productId) params[URL_PARAMS.PRODUCT_ID] = productId;
    if (recipeId) params[URL_PARAMS.RECIPE_ID] = recipeId;

    const currentParams = Object.fromEntries(searchParams.entries());
    const newParams = { ...currentParams, ...params };

    // Only update if params changed
    if (JSON.stringify(currentParams) !== JSON.stringify(newParams)) {
      setSearchParams(newParams, { replace: true });
    }
  }, [productId, recipeId]);

  /**
   * Set product ID and update URL
   */
  const setProductId = useCallback((id, name = '') => {
    setProductIdState(id);
    setProductName(name);

    if (!id) {
      // Clear recipe when product is cleared
      setRecipeIdState(null);
      setRecipeName('');
    }
  }, []);

  /**
   * Set recipe ID and update URL
   */
  const setRecipeId = useCallback((id, name = '') => {
    setRecipeIdState(id);
    setRecipeName(name);
  }, []);

  /**
   * Navigate to recipe with state
   */
  const navigateToRecipe = useCallback((recipeId, recipeName, additionalState = {}) => {
    navigate(location.pathname, {
      replace: false,
      state: {
        ...location.state,
        recipeId,
        recipeName,
        productId,
        ...additionalState,
      },
    });
    setRecipeIdState(recipeId);
    setRecipeName(recipeName);
  }, [navigate, location, productId]);

  /**
   * Navigate to product with state
   */
  const navigateToProduct = useCallback((productId, productName, additionalState = {}) => {
    navigate(location.pathname, {
      replace: false,
      state: {
        ...location.state,
        productId,
        productName,
        ...additionalState,
      },
    });
    setProductIdState(productId);
    setProductName(productName);
  }, [navigate, location]);

  /**
   * Clear all selections and go back to product list
   */
  const clearSelection = useCallback(() => {
    setProductIdState(null);
    setRecipeIdState(null);
    setRecipeName('');
    setProductName('');
    setLastProcessOpId(0);
    setProcessCount(0);
    setEditProcessData(null);

    // Clear URL params
    setSearchParams({}, { replace: true });
  }, [setSearchParams]);

  /**
   * Set edit process data (replaces localStorage.setItem('editProcess', ...))
   */
  const setEditProcess = useCallback((processData) => {
    setEditProcessData(processData);
  }, []);

  /**
   * Get edit process data (replaces localStorage.getItem('editProcess'))
   */
  const getEditProcess = useCallback(() => {
    return editProcessData;
  }, [editProcessData]);

  /**
   * Clear edit process data
   */
  const clearEditProcess = useCallback(() => {
    setEditProcessData(null);
  }, []);

  /**
   * Update last process operation ID
   */
  const updateLastProcessOpId = useCallback((opId) => {
    setLastProcessOpId(opId || 0);
  }, []);

  /**
   * Update process count
   */
  const updateProcessCount = useCallback((count) => {
    setProcessCount(count || 0);
  }, []);

  const value = {
    // State
    productId,
    recipeId,
    recipeName,
    productName,
    lastProcessOpId,
    processCount,
    editProcessData,

    // Setters
    setProductId,
    setRecipeId,
    setRecipeName,
    setProductName,
    updateLastProcessOpId,
    updateProcessCount,

    // Process editing
    setEditProcess,
    getEditProcess,
    clearEditProcess,

    // Navigation helpers
    navigateToRecipe,
    navigateToProduct,
    clearSelection,

    // Utility
    hasProduct: !!productId,
    hasRecipe: !!recipeId,
  };

  return (
    <RecipeContext.Provider value={value}>
      {children}
    </RecipeContext.Provider>
  );
};

export default RecipeContext;
